import {
  format,
  parseISO,
  isSameDay,
  subDays,
  startOfDay,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  eachDayOfInterval,
  isToday
} from "date-fns"
import { cs } from "date-fns/locale"
import type { DailyEntry as StorageDailyEntry } from "./storage"
import type { DailyEntry as DBDailyEntry } from "./db/schema"
import { getZoneNumericValue } from "./zones"

// Support both storage and DB entry types
type DailyEntry = StorageDailyEntry | DBDailyEntry

/**
 * Get local date string without timezone conversion
 * ✅ Correct: "2025-01-14" (regardless of timezone)
 * ❌ Avoid: date.toISOString().split("T")[0] - causes timezone bugs!
 */
export function toLocalDateString(date: Date): string {
  return format(date, "yyyy-MM-dd")
}

/**
 * Parse ISO date string to Date object
 */
export function fromLocalDateString(dateStr: string): Date {
  return parseISO(dateStr)
}

/**
 * Get current date as local date string
 */
export function getTodayDateString(): string {
  return toLocalDateString(new Date())
}

/**
 * Find entry for a specific date
 */
export function getEntryForDate(
  entries: DailyEntry[],
  date: Date
): DailyEntry | undefined {
  const dateStr = toLocalDateString(date)
  return entries.find((e) => e.date === dateStr)
}

/**
 * Get last N days with their entries (optimized with Map)
 */
export function getLastNDays(
  entries: DailyEntry[],
  days: number
): Array<{ date: Date; entry: DailyEntry | undefined }> {
  // Create Map for O(1) lookups instead of O(n)
  const entriesMap = new Map<string, DailyEntry>()
  entries.forEach((entry) => {
    entriesMap.set(entry.date, entry)
  })

  const today = startOfDay(new Date())
  const result: Array<{ date: Date; entry: DailyEntry | undefined }> = []

  for (let i = days - 1; i >= 0; i--) {
    const date = subDays(today, i)
    const dateStr = toLocalDateString(date)
    const entry = entriesMap.get(dateStr)
    result.push({ date, entry })
  }

  return result
}

/**
 * Format date in Czech locale for display
 * Example: "pondělí, 14. ledna"
 */
export function formatCzechDate(date: Date): string {
  return format(date, "EEEE, d. MMMM", { locale: cs })
}

/**
 * Format date with year in Czech locale
 * Example: "14. ledna 2025"
 */
export function formatCzechDateWithYear(date: Date): string {
  return format(date, "d. MMMM yyyy", { locale: cs })
}

/**
 * Format month and year in Czech
 * Example: "leden 2025"
 */
export function formatCzechMonthYear(date: Date): string {
  return format(date, "LLLL yyyy", { locale: cs })
}

/**
 * Get Czech weekday abbreviation (single letter)
 * Example: "P" for pondělí (Monday)
 */
export function getCzechWeekdayAbbr(date: Date): string {
  const weekdays = ["N", "P", "Ú", "S", "Č", "P", "S"]
  return weekdays[date.getDay()]
}

/**
 * Check if date has an entry
 */
export function hasEntry(entries: DailyEntry[], date: Date): boolean {
  return getEntryForDate(entries, date) !== undefined
}

/**
 * Check if two dates are the same day (ignores time)
 */
export function isSameDayAs(date1: Date, date2: Date): boolean {
  return isSameDay(date1, date2)
}

// ============================================================================
// Calendar-specific utilities (Task 22)
// ============================================================================

/**
 * Get the start of the week for a given date (Monday as first day)
 * Uses Czech locale which has Monday as the first day of week
 */
export function getWeekStart(date: Date = new Date()): Date {
  return startOfWeek(date, { locale: cs, weekStartsOn: 1 })
}

/**
 * Get the end of the week for a given date (Sunday as last day)
 * Uses Czech locale which has Monday as the first day of week
 */
export function getWeekEnd(date: Date = new Date()): Date {
  return endOfWeek(date, { locale: cs, weekStartsOn: 1 })
}

/**
 * Get all days in the current week (Monday to Sunday)
 * Returns array of 7 dates starting from Monday
 */
export function getCurrentWeek(date: Date = new Date()): Date[] {
  const weekStart = getWeekStart(date)
  const weekEnd = getWeekEnd(date)
  return eachDayOfInterval({ start: weekStart, end: weekEnd })
}

/**
 * Get focused week view (3 days before + today + 3 days after)
 * For mobile-first 7-day view centered on current date
 */
export function getFocusedWeek(centerDate: Date = new Date()): Date[] {
  const result: Date[] = []

  // 3 days before
  for (let i = 3; i > 0; i--) {
    result.push(subDays(centerDate, i))
  }

  // Today (center date)
  result.push(centerDate)

  // 3 days after
  for (let i = 1; i <= 3; i++) {
    result.push(addWeeks(centerDate, 0))
    // Add i days to center date
    result[result.length - 1] = subDays(centerDate, -i)
  }

  return result
}

/**
 * Navigate to previous week
 */
export function getPreviousWeek(date: Date = new Date()): Date {
  return subWeeks(date, 1)
}

/**
 * Navigate to next week
 */
export function getNextWeek(date: Date = new Date()): Date {
  return addWeeks(date, 1)
}

/**
 * Get Czech weekday name (short form)
 * Example: "Po" for Monday, "Út" for Tuesday
 */
export function getCzechWeekdayShort(date: Date): string {
  return format(date, "EEEEEE", { locale: cs })
}

/**
 * Get Czech weekday name (full)
 * Example: "Pondělí" for Monday
 */
export function getCzechWeekdayFull(date: Date): string {
  return format(date, "EEEE", { locale: cs })
}

/**
 * Check if a date is today
 */
export function isTodayDate(date: Date): boolean {
  return isToday(date)
}

/**
 * Format date for calendar cell display
 * Example: "14" (just the day number)
 */
export function formatDayNumber(date: Date): string {
  return format(date, "d")
}

/**
 * Format date for calendar header
 * Example: "Listopad 2025"
 */
export function formatCalendarHeader(date: Date): string {
  return format(date, "LLLL yyyy", { locale: cs })
}

/**
 * Get week number of the year
 * Example: Week 46 of 2025
 */
export function getWeekNumber(date: Date): number {
  return parseInt(format(date, "I", { locale: cs }))
}

// ============================================================================
// Streak calculation and daily statistics (Task 23)
// ============================================================================

/**
 * Group entries by date for efficient lookups
 * Returns Map<dateString, DailyEntry[]> for O(1) lookups
 */
export function groupEntriesByDay(entries: DailyEntry[]): Map<string, DailyEntry[]> {
  const grouped = new Map<string, DailyEntry[]>()

  entries.forEach((entry) => {
    const dateStr = entry.date
    const existing = grouped.get(dateStr) || []
    existing.push(entry)
    grouped.set(dateStr, existing)
  })

  return grouped
}

/**
 * Calculate current streak (consecutive days with ANY entry)
 * Only counts current active streak, not historical longest
 */
export function calculateCurrentStreak(entries: DailyEntry[]): number {
  if (entries.length === 0) return 0

  // Group entries by date
  const entriesByDate = groupEntriesByDay(entries)

  // Get unique dates sorted descending (newest first)
  const uniqueDates = Array.from(entriesByDate.keys()).sort().reverse()

  // Start from today and count backwards
  const today = getTodayDateString()
  const yesterday = toLocalDateString(subDays(new Date(), 1))
  let streak = 0
  let checkDate: Date

  // Check if there's an entry for today
  if (entriesByDate.has(today)) {
    // Start counting from today
    checkDate = new Date()
  } else if (entriesByDate.has(yesterday)) {
    // No entry today, but there is one yesterday - count from yesterday
    checkDate = subDays(new Date(), 1)
  } else {
    // No entry today or yesterday = streak is broken
    return 0
  }

  // Count consecutive days backwards from checkDate
  while (entriesByDate.has(toLocalDateString(checkDate))) {
    streak++
    checkDate = subDays(checkDate, 1)
  }

  return streak
}

/**
 * Calculate daily average zone from multiple entries
 * Returns average zone ID as number, or null if no entries
 */
export function calculateDailyAverage(entries: DailyEntry[]): number | null {
  if (entries.length === 0) return null

  // Use shared utility for consistent zone mapping
  const zoneNumbers = entries.map((entry) => getZoneNumericValue(entry.zoneId))

  if (zoneNumbers.length === 0) return null

  const sum = zoneNumbers.reduce((acc, num) => acc + num, 0)
  const average = sum / zoneNumbers.length

  return Math.round(average)
}

/**
 * Get zone ID string from average number
 * Converts average back to zone ID format ("1" to "10" or "R")
 */
export function averageToZoneId(average: number | null): string | null {
  if (average === null) return null
  if (average === 0) return "R"
  if (average < 1) return "1"
  if (average > 10) return "10"
  return average.toString()
}

/**
 * Get all entries for a specific date
 */
export function getEntriesForDate(entries: DailyEntry[], date: Date): DailyEntry[] {
  const dateStr = toLocalDateString(date)
  return entries.filter((entry) => entry.date === dateStr)
}

/**
 * Get count of entries for a specific date
 */
export function getEntryCount(entries: DailyEntry[], date: Date): number {
  return getEntriesForDate(entries, date).length
}

/**
 * Check if a date has any entries
 */
export function hasEntries(entries: DailyEntry[], date: Date): boolean {
  return getEntryCount(entries, date) > 0
}
